import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addMinutes, parse, format } from "date-fns";
import {
  ActivityFormValues,
  activitySchema,
  defaultValues,
} from "../zod/activitySchema";
import type { ActivityFragment } from "../../../../../../../../../__generated__/graphql";
import { formatDateForInput } from "../../../../../../../../../lib/utils";
import {
  formatActivityRemindersFromIsoDate,
  type Reminder,
} from "../utils/reminderUtils";
import type { DocumentMetadata, DocumentError } from "../types";
import type { ContactMetadata } from "../components/ContactsSection";

interface UseActivityFormParams {
  defaultActivity?: ActivityFragment | null;
  eventDetailsData?: {
    event?: {
      startDate?: string;
      eventLocation?: {
        location: {
          address?: string;
          city?: string;
          countryCode?: string;
          label?: string;
          zipCode?: string;
          latitude?: number | null;
          longitude?: number | null;
          photoUrls?: string[] | null;
        };
      } | null;
    } | null;
  };
  isDuplicate?: boolean;
  defaultStartDate?: string; // Format: YYYY-MM-DD
  defaultStartTime?: string; // Format: HH:mm
}

export function useActivityForm({
  defaultActivity,
  eventDetailsData,
  defaultStartDate,
  defaultStartTime,
}: UseActivityFormParams) {
  // UI section toggles
  const [sectionVisibility, setSectionVisibility] = useState({
    description: false,
    location: false,
    documents: false,
    endTime: false,
    reminders: false,
    contacts: false,
  });

  const toggleSection = (
    key: keyof typeof sectionVisibility,
    value?: boolean
  ) => {
    setSectionVisibility((prev) => ({
      ...prev,
      [key]: value ?? !prev[key],
    }));
  };

  // Icon/color state
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  // Complex data states
  const [documents, setDocuments] = useState<DocumentMetadata[]>([]);
  const [documentErrors, setDocumentErrors] = useState<DocumentError[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [contacts, setContacts] = useState<ContactMetadata[]>([]);

  const form = useForm<ActivityFormValues>({
    resolver: zodResolver(activitySchema),
    defaultValues,
  });

  const { reset, setValue, watch, clearErrors } = form;

  // Watch startTime changes
  const startTime = watch("startTime");
  const endTime = watch("endTime");

  // Watch startDay and endDay changes
  const startDay = watch("startDay");
  const endDay = watch("endDay");

  // Add effect to update endTime when startTime changes and clear errors
  useEffect(() => {
    if (startTime && !endTime) {
      try {
        const timeDate = parse(startTime, "HH:mm", new Date());
        const endTimeDate = addMinutes(timeDate, 30);
        const newEndTime = format(endTimeDate, "HH:mm");
        setValue("endTime", newEndTime);
        clearErrors("endTime");
      } catch (error) {
        console.error("Error updating end time:", error);
      }
    }
  }, [startTime, endTime, setValue, clearErrors]);

  // Add effect to clear errors when endTime is manually changed
  useEffect(() => {
    if (endTime) {
      clearErrors("endTime");
    }
  }, [endTime, clearErrors]);

  // Set endDay to startDay when end time section is displayed and endDay is empty
  useEffect(() => {
    if (sectionVisibility.endTime && startDay && !endDay) {
      setValue("endDay", startDay);
    }
  }, [sectionVisibility.endTime, startDay, endDay, setValue]);

  // Update endDay to match startDay if startDay becomes greater than endDay
  useEffect(() => {
    if (startDay && endDay && startDay > endDay) {
      setValue("endDay", startDay);
    }
  }, [startDay, endDay, setValue]);

  const getDefaultActivityValues = (activity: ActivityFragment) => {
    const formattedStart = formatDateForInput(activity.startDate);
    const formattedEnd = formatDateForInput(activity.endDate);

    if (activity.description) {
      setSectionVisibility((prev) => ({ ...prev, description: true }));
    }

    if (activity.activityLocation) {
      setSectionVisibility((prev) => ({ ...prev, location: true }));
    }

    if (activity.documents?.length && activity.documents.length > 0) {
      setSectionVisibility((prev) => ({ ...prev, documents: true }));
    }

    if (activity.endDate) {
      setSectionVisibility((prev) => ({ ...prev, endTime: true }));
    }

    if (activity.contacts?.length && activity.contacts.length > 0) {
      setSectionVisibility((prev) => ({ ...prev, contacts: true }));
      setContacts(
        activity.contacts.map((c) => ({
          id: c.id,
          name: c.name,
          email: c.email || "",
          phoneNumber: c.phoneNumber || "",
          description: c.description || "",
        }))
      );
    }

    if (
      activity.activityReminders?.length &&
      activity.activityReminders.length > 0
    ) {
      const formattedReminders = activity.activityReminders
        .map((r) => formatActivityRemindersFromIsoDate(r, activity.startDate))
        .filter((r) => r !== null) as Reminder[];
      setSectionVisibility((prev) => ({
        ...prev,
        reminders: formattedReminders.length > 0,
      }));
      setReminders(formattedReminders);
    }

    setSelectedIcon(activity.icon);
    setSelectedColor(activity.color);

    if (activity.documents?.length && activity.documents.length > 0) {
      setDocuments(
        activity.documents.map((doc) => ({
          id: doc.id,
          title: doc.title,
          description: doc.description || "",
          file: null,
          bucketKey: doc.bucketKey,
          isPublic: doc.isPublic,
          mimeType: doc.mimeType,
          selectedParticipants:
            doc.documentSharings?.map((sharing) => ({
              value: sharing.recipientUserOrganizationId,
              organizationId: sharing.recipientOrganizationId,
              userOrganizationId: sharing.recipientUserOrganizationId,
              label: sharing.recipientPublicUserOrganization.name,
            })) || [],
        }))
      );
    }

    return {
      title: activity.title,
      description: activity.description || "",
      color: activity.color,
      icon: activity.icon,
      startDay: formattedStart?.day || "",
      startTime: formattedStart?.time || "",
      endDay: formattedEnd?.day || formattedStart?.day || "",
      endTime: formattedEnd?.time || "",
      participants:
        activity.activityParticipants?.map((p) => ({
          value: p.publicUserOrganization.id,
          organizationId: p.publicUserOrganization.organizationId,
          userOrganizationId: p.publicUserOrganization.id,
          label: p.publicUserOrganization.name,
        })) || [],
      groupParticipants:
        activity.activityGroupParticipants?.map((gp) => ({
          id: gp.userGroup.id,
          name: gp.userGroup.name,
        })) || [],
      locationAddress: activity.activityLocation?.location.address || "",
      locationCity: activity.activityLocation?.location.city || "",
      locationCountryCode:
        activity.activityLocation?.location.countryCode || "FR",
      locationLabel: activity.activityLocation?.location.label || "",
      locationZipCode: activity.activityLocation?.location.zipCode || "",
      locationLatitude: activity.activityLocation?.location.latitude || 0,
      locationLongitude: activity.activityLocation?.location.longitude || 0,
      locationPhotoUrls: activity.activityLocation?.location.photoUrls || [],
      contacts:
        activity.contacts?.map((c) => ({
          id: c.id,
          description: c.description || "",
          name: c.name,
          email: c.email || "",
          phoneNumber: c.phoneNumber || "",
        })) || [],
    };
  };

  // Update the useEffect that sets initial values
  useEffect(() => {
    let values = defaultValues;

    // Apply explicit default start date/time if provided (lowest priority)
    if (defaultStartDate) {
      values = {
        ...values,
        startDay: defaultStartDate,
        endDay: defaultStartDate,
      };
    }
    if (defaultStartTime) {
      values = {
        ...values,
        startTime: defaultStartTime,
      };
    }

    // Apply event data defaults if available (higher priority than explicit defaults)
    if (eventDetailsData?.event?.startDate) {
      const formattedStart = formatDateForInput(
        eventDetailsData.event.startDate
      );

      if (formattedStart) {
        values = {
          ...values,
          startDay: formattedStart.day,
          endDay: formattedStart.day,
        };
      }
    }

    if (
      eventDetailsData?.event?.eventLocation &&
      !defaultActivity?.activityLocation
    ) {
      values = {
        ...values,
        locationAddress:
          eventDetailsData.event.eventLocation.location.address || "",
        locationCity: eventDetailsData.event.eventLocation.location.city || "",
        locationCountryCode:
          eventDetailsData.event.eventLocation.location.countryCode || "FR",
        locationLabel:
          eventDetailsData.event.eventLocation.location.label || "",
        locationZipCode:
          eventDetailsData.event.eventLocation.location.zipCode || "",
        locationLatitude:
          eventDetailsData.event.eventLocation.location.latitude || 0,
        locationLongitude:
          eventDetailsData.event.eventLocation.location.longitude || 0,
        locationPhotoUrls:
          eventDetailsData.event.eventLocation.location.photoUrls || [],
      };
    }

    // Apply default activity values if editing (highest priority)
    if (defaultActivity) {
      values = getDefaultActivityValues(defaultActivity);
    }

    reset(values);
  }, [
    eventDetailsData,
    defaultActivity,
    reset,
    defaultStartDate,
    defaultStartTime,
  ]);

  // Reset all state on close
  const resetFormState = () => {
    setSelectedIcon(null);
    setSelectedColor(null);
    setSectionVisibility({
      description: false,
      location: false,
      documents: false,
      endTime: false,
      reminders: false,
      contacts: false,
    });
    setDocuments([]);
    setReminders([]);
    setContacts([]);
    setDocumentErrors([]);
    reset();
  };

  return {
    form,
    sectionVisibility,
    toggleSection,
    selectedIcon,
    setSelectedIcon,
    selectedColor,
    setSelectedColor,
    documents,
    setDocuments,
    documentErrors,
    setDocumentErrors,
    reminders,
    setReminders,
    contacts,
    setContacts,
    resetFormState,
  };
}
